import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Alert,
  IconButton,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import { ApplicationFormModal } from "../../components/Modals/ApplicationFormModal";
import ApplicationFormTemplate from "../../components/templates/ApplicationFormTemplate";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { ApplicationFormData, useApplicationForm } from "../../hooks/useApplicationForm";
import { useSessionClass } from "../../contexts/SessionClassContext";
import { useSchools } from "../../hooks/useSchools";
import { useClasses } from "../../hooks/useClasses";
import { useSessions } from "../../hooks/useSession";
import { useError } from "../../contexts/ErrorContext";
import { useTenantHeader } from "../../hooks/useTenantHeader";
import { useCurrentStudent } from "../../hooks/useCurrentStudent";
import { useStudents } from "../../hooks/useStudents";
import { useSubjectsByClassId } from "../../hooks/useSubjects";
import { useQueryClient } from "@tanstack/react-query";

const StudentProfile: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showError, showSuccess } = useError();
  const { selectedClass, selectedSession, setSelectedClass, setSelectedSession } = useSessionClass();
  const { tenantLoading } = useTenantHeader();
  const queryClient = useQueryClient();
  // Don't fetch all schools - only use useSchoolById for students
  const { useSchoolById } = useSchools(tenantLoading, { enableQuery: false });

  // Get schoolId from login response
  const schoolId = user?.schoolId ?? 0;

  // Fetch current student using mobile number (email) from login response
  const { studentData, studentLoading, studentError, refetchStudent } = useCurrentStudent();

  // Format dateOfBirth for API call (convert array to string format if needed)
  const formattedDateOfBirth = useMemo(() => {
    if (!studentData?.dateOfBirth) return undefined;
    if (typeof studentData.dateOfBirth === 'string') {
      return studentData.dateOfBirth;
    }
    if (Array.isArray(studentData.dateOfBirth) && studentData.dateOfBirth.length >= 3) {
      // Format as YYYY-MM-DD for API
      const [year, month, day] = studentData.dateOfBirth;
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
    return undefined;
  }, [studentData?.dateOfBirth]);

  // Fetch original application form using mobile number and DOB only (for students, don't use schoolId to prevent data inconsistency)
  // Pass undefined for schoolId to ensure no schoolId-based queries are triggered
  const { applicationFormsByMobileNoAndDOBQuery } = useApplicationForm(
    tenantLoading,
    undefined, // Don't pass schoolId for students - only use mobile/DOB query to prevent seeing all application forms
    studentData?.mobileNo,
    formattedDateOfBirth
  );
  const originalApplicationForm = applicationFormsByMobileNoAndDOBQuery.data;

  // Fetch school data using schoolId from login response
  const { data: schoolData } = useSchoolById(schoolId);

  // Fetch classes and sessions by schoolId
  const { classesQuery } = useClasses(schoolId, tenantLoading);
  const { sessionsQuery } = useSessions(schoolId);
  const classesData = classesQuery.data || [];
  const sessionsData = sessionsQuery.data || [];

  // Auto-select class and session based on student's classId and sessionId
  useEffect(() => {
    if (studentData && classesData.length > 0 && sessionsData.length > 0) {
      // Find and set the class from student's schoolClassId
      if (studentData.schoolClassId && !selectedClass) {
        const studentClass = classesData.find((c: any) => c.id === studentData.schoolClassId);
        if (studentClass) {
          setSelectedClass(studentClass);
        }
      }

      // Find and set the session from student's sessionId
      if (studentData.sessionId && !selectedSession) {
        const studentSession = sessionsData.find((s: any) => s.id === studentData.sessionId);
        if (studentSession) {
          setSelectedSession(studentSession);
        }
      }
    }
  }, [studentData, classesData, sessionsData, selectedClass, selectedSession, setSelectedClass, setSelectedSession]);

  // Get the selected class and session (use student's data if not selected)
  const studentClass = selectedClass || (classesData || []).find(
    (c: any) => c.id === studentData?.schoolClassId
  );

  // Fetch subjects by classId (from student object)
  const { data: subjectsData } = useSubjectsByClassId(studentData?.schoolClassId || 0, tenantLoading);
  const availableSubjects = subjectsData || [];

  // Convert Student to ApplicationFormData format for compatibility with existing components
  const applicationForm = useMemo(() => {
    if (!studentData) return null;

    // Parse student subjects
    const studentSubjects = (() => {
      if (Array.isArray(studentData.subjects)) {
        return studentData.subjects;
      }
      if (typeof studentData.subjects === 'string') {
        return studentData.subjects.split(',').map(s => s.trim()).filter(Boolean);
      }
      return [];
    })();

    // Map student subjects to available subjects for proper subject codes
    const mappedSubjects = studentSubjects.map((subjectCodeOrName: string) => {
      // Try to find matching subject in availableSubjects
      const matchedSubject = availableSubjects.find((subj: any) =>
        subj.subjectCode?.toLowerCase() === subjectCodeOrName.toLowerCase() ||
        subj.name?.toLowerCase() === subjectCodeOrName.toLowerCase()
      );
      return matchedSubject?.subjectCode || subjectCodeOrName;
    });

    // Transform Student object to ApplicationFormData format
    // Use original application form ID if available (for correct photo fetching), otherwise use student ID
    const applicationFormId = originalApplicationForm?.id || studentData.id;

    return {
      id: studentData.id, // Use student ID
      studentId: studentData.id, // Explicitly pass studentId so ApplicationFormTemplate uses student photo API
      applicationNo: studentData.applicationNo || '',
      rollNo: studentData.rollNumber || '',
      enrolmentNo: studentData.enrolmentNo || '',
      scholarNo: studentData.scholarNo || '',
      sssmid: studentData.sssmid || '',
      penNo: studentData.penNo || '',
      apparId: studentData.apparId || '',
      studentCode: studentData.studentCode || '',
      studentName: studentData.studentName || '',
      fatherName: studentData.fatherName || '',
      motherName: studentData.motherName || '',
      dateOfBirth: (() => {
        if (typeof studentData.dateOfBirth === 'string') {
          return studentData.dateOfBirth;
        }
        if (Array.isArray(studentData.dateOfBirth)) {
          return `${studentData.dateOfBirth[0]}-${String(studentData.dateOfBirth[1]).padStart(2, '0')}-${String(studentData.dateOfBirth[2]).padStart(2, '0')}`;
        }
        return '';
      })(),
      gender: studentData.gender || '',
      medium: studentData.medium || '',
      religion: studentData.religion || '',
      caste: studentData.caste || '',
      subCaste: studentData.subCaste || '',
      casteCertificate: studentData.casteCertificate || '',
      aadharNo: studentData.aadharNo || '',
      // Include photo and signature from API response if available
      studentPhoto: studentData.studentPhoto || null,
      studentSignature: studentData.studentSignature || null,
      houseNo: studentData.houseNo || '',
      street: studentData.street || '',
      landmark: studentData.landmark || '',
      area: studentData.area || '',
      city: studentData.city || '',
      postOffice: studentData.postOffice || '',
      district: studentData.district || '',
      state: studentData.state || '',
      pinCode: studentData.pinCode || '',
      mobileNo: studentData.mobileNo || '',
      gmail: studentData.gmail || '',
      bankName: studentData.bankName || '',
      ifcCode: studentData.ifcCode || '',
      accountNo: studentData.accountNo || '',
      annualIncome: studentData.annualIncome || '',
      admissionDate: (() => {
        if (typeof studentData.admissionDate === 'string') {
          return studentData.admissionDate;
        }
        if (Array.isArray(studentData.admissionDate)) {
          return `${studentData.admissionDate[0]}-${String(studentData.admissionDate[1]).padStart(2, '0')}-${String(studentData.admissionDate[2]).padStart(2, '0')}`;
        }
        return '';
      })(),
      admissionClass: studentData.admissionClass || '',
      section: studentData.section || '',
      admissionType: studentData.admissionType || '',
      cwsnCode: studentData.cwsnCode || '',
      sambalKramank: studentData.sambalKramank || '',
      subjects: mappedSubjects,
      schoolId: studentData.schoolId || 0,
      sessionId: studentData.sessionId || 0,
      classId: studentData.schoolClassId || 0,
      sessionName: studentData.sessionName || '',
      stream: studentData.stream || '',
      isRegistered: studentData.status ? 'approved' : 'pending',
    } as ApplicationFormData;
  }, [studentData, availableSubjects, originalApplicationForm]);

  const [viewApplicationFormOpen, setViewApplicationFormOpen] = useState(false);
  const [updateProfileOpen, setUpdateProfileOpen] = useState(false);

  const handleViewApplicationForm = () => {
    if (!applicationForm) {
      showError(t("application_form_not_found"));
      return;
    }
    setViewApplicationFormOpen(true);
  };

  const handleUpdateProfile = async () => {
    if (!applicationForm) {
      showError(t("application_form_not_found"));
      return;
    }
    // Refetch student data to get latest subjects before opening the modal
    try {
      await refetchStudent();
    } catch (error) {
    }
    setUpdateProfileOpen(true);
  };

  // Only get the mutation, don't fetch all students - students use useCurrentStudent
  const { updateStudentWithDTOMutation } = useStudents(tenantLoading, schoolId, false);

  const handleUpdateProfileSubmit = async (formData: ApplicationFormData) => {
    if (!studentData?.id) {
      showError(t("student_data_not_found") || "Student data not found");
      return;
    }
    try {
      // Convert ApplicationFormData back to Student format for update
      const studentUpdateData = {
        studentCode: formData.studentCode || studentData.studentCode,
        applicationNo: formData.applicationNo || studentData.applicationNo,
        rollNumber: formData.rollNo || studentData.rollNumber,
        enrolmentNo: formData.enrolmentNo || studentData.enrolmentNo,
        studentName: formData.studentName || studentData.studentName,
        fatherName: formData.fatherName || studentData.fatherName,
        motherName: formData.motherName || studentData.motherName,
        dateOfBirth: formData.dateOfBirth || studentData.dateOfBirth,
        gender: formData.gender || studentData.gender,
        medium: formData.medium || studentData.medium,
        caste: formData.caste || studentData.caste,
        subCaste: formData.subCaste || studentData.subCaste,
        casteCertificate: formData.casteCertificate || studentData.casteCertificate,
        religion: formData.religion || studentData.religion,
        sssmid: formData.sssmid || studentData.sssmid,
        scholarNo: formData.scholarNo || studentData.scholarNo,
        penNo: formData.penNo || studentData.penNo,
        apparId: formData.apparId || studentData.apparId,
        aadharNo: formData.aadharNo || studentData.aadharNo,
        houseNo: formData.houseNo || studentData.houseNo,
        street: formData.street || studentData.street,
        landmark: formData.landmark || studentData.landmark,
        area: formData.area || studentData.area,
        city: formData.city || studentData.city,
        postOffice: formData.postOffice || studentData.postOffice,
        district: formData.district || studentData.district,
        state: formData.state || studentData.state,
        pinCode: formData.pinCode || studentData.pinCode,
        mobileNo: formData.mobileNo || studentData.mobileNo,
        bankName: formData.bankName || studentData.bankName,
        ifcCode: formData.ifcCode || studentData.ifcCode,
        accountNo: formData.accountNo || studentData.accountNo,
        annualIncome: formData.annualIncome || studentData.annualIncome,
        section: formData.section || studentData.section,
        subjects: Array.isArray(formData.subjects) ? formData.subjects.join(',') : formData.subjects || studentData.subjects,
        gmail: formData.gmail || studentData.gmail,
        admissionDate: formData.admissionDate || studentData.admissionDate,
        admissionClass: formData.admissionClass || studentData.admissionClass,
        admissionType: formData.admissionType || studentData.admissionType,
        stream: formData.stream || studentData.stream || '', // Stream field with default empty string
        schoolId: formData.schoolId || studentData.schoolId,
        schoolClassId: formData.classId || studentData.schoolClassId,
        sessionName: formData.sessionName || studentData.sessionName,
        sessionId: formData.sessionId || studentData.sessionId,
        className: studentData.className || '',
        cwsnCode: formData.cwsnCode || studentData.cwsnCode,
        sambalKramank: formData.sambalKramank || studentData.sambalKramank,
        status: formData.isRegistered === 'approved' || studentData.status,
        // Include photo and signature File objects if they exist
        studentPhoto: formData.studentPhoto || undefined,
        studentSignature: formData.studentSignature || undefined,
      };

      await updateStudentWithDTOMutation.mutateAsync({
        id: studentData.id,
        data: studentUpdateData,
      });
      showSuccess(t("profile_updated_successfully"));
      setUpdateProfileOpen(false);
    } catch (error: any) {
      showError(
        t("failed_to_update_profile")?.replace(
          "{error}",
          error?.response?.data?.message || error?.message || "Unknown error"
        ) || error?.response?.data?.message || error?.message || "Failed to update profile"
      );
    }
  };

  return (
    <>
      <Paper sx={{ p: 2, maxWidth: 1500, mx: "auto", mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {t("profile") || "Profile"}
          </Typography>
        </Box>

        {(() => {
          if (studentLoading) {
            return (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            );
          }
          if (studentError) {
            return (
              <Alert severity="error" sx={{ mb: 2 }}>
                {t("error_loading_profile") || "Error loading profile"}
              </Alert>
            );
          }
          if (!studentData) {
            return (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t("student_data_not_found") || "Student data not found"}
              </Alert>
            );
          }
          return (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: 3,
                    borderRadius: 2,
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: "#3092e3",
                          borderRadius: "50%",
                          p: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <DescriptionIcon sx={{ color: "white", fontSize: 32 }} />
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" color="#3092e3">
                          {t("view_application_form")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("view_application_form_description")}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleViewApplicationForm}
                      disabled={!applicationForm}
                      sx={{
                        backgroundColor: "#3092e3",
                        "&:hover": { backgroundColor: "#2573c1" },
                        py: 1.5,
                        fontSize: "1rem",
                        fontWeight: "bold",
                      }}
                      startIcon={<DescriptionIcon />}
                    >
                      {t("view_application_form")}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: 3,
                    borderRadius: 2,
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: "#28a745",
                          borderRadius: "50%",
                          p: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <EditIcon sx={{ color: "white", fontSize: 32 }} />
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" color="#28a745">
                          {t("update_profile")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("update_profile_description")}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleUpdateProfile}
                      disabled={!applicationForm}
                      sx={{
                        backgroundColor: "#28a745",
                        "&:hover": { backgroundColor: "#218838" },
                        py: 1.5,
                        fontSize: "1rem",
                        fontWeight: "bold",
                      }}
                      startIcon={<EditIcon />}
                    >
                      {t("update_profile")}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            </Grid>
          );
        })()}
      </Paper>

      {/* View Application Form Dialog */}
      {applicationForm && (
        <Dialog
          open={viewApplicationFormOpen}
          onClose={() => {
            // Invalidate photo queries when closing to ensure fresh data on next open
            if (applicationForm?.id) {
              queryClient.invalidateQueries({
                queryKey: ["applicationPhotoInfo", applicationForm.id],
              });
              queryClient.invalidateQueries({
                queryKey: ["applicationPhoto"],
              });
            }
            setViewApplicationFormOpen(false);
          }}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: '90vh',
              overflow: 'auto',
            }
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pb: 1,
            }}
          >
            <Typography variant="h6" component="span">
              {t("view_application_form")}
            </Typography>
            <IconButton
              onClick={() => {
                // Invalidate photo queries when closing to ensure fresh data on next open
                if (applicationForm?.id) {
                  queryClient.invalidateQueries({
                    queryKey: ["applicationPhotoInfo", applicationForm.id],
                  });
                  queryClient.invalidateQueries({
                    queryKey: ["applicationPhoto"],
                  });
                }
                setViewApplicationFormOpen(false);
              }}
              sx={{
                color: (theme) => theme.palette.grey[500],
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.action.hover,
                },
              }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <ApplicationFormTemplate
              key={`${applicationForm.id}-${viewApplicationFormOpen}`} // Force re-render when modal opens/closes
              data={applicationForm as any}
              schoolData={schoolData}
              renderForPDF={false}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Update Profile Modal */}
      {applicationForm && (
        <ApplicationFormModal
          key={`student-profile-${studentData?.id}-${studentData?.subjects?.length || 0}`}
          open={updateProfileOpen}
          onClose={() => setUpdateProfileOpen(false)}
          onSubmit={handleUpdateProfileSubmit}
          initialData={{
            ...applicationForm,
            sessionId: applicationForm.sessionId || 0,
          } as any}
          isEdit={true}
          isStudentEdit={true}
          isStudentProfileUpdate={true}
          isReadOnly={false}
          schoolData={schoolData}
          selectedClass={studentClass}
        />
      )}
    </>
  );
};

export default StudentProfile;

